import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, Briefcase, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// ─── Constants ──────────────────────────────────────────────────────────────

const STAGE_STYLES = {
    initial_review: "bg-slate-100 text-slate-700 border-slate-300",
    diagnosis_complete: "bg-sky-100 text-sky-800 border-sky-300",
    engaged: "bg-blue-100 text-blue-800 border-blue-300",
    structuring: "bg-amber-100 text-amber-800 border-amber-300",
    completed: "bg-green-100 text-green-800 border-green-300",
    lost: "bg-red-100 text-red-700 border-red-300",
    withdrawn: "bg-slate-100 text-slate-500 border-slate-300",
};

const STAGE_LABELS = {
    initial_review: "Initial Review",
    diagnosis_complete: "Diagnosis Complete",
    engaged: "Engaged",
    structuring: "Structuring",
    completed: "Completed",
    lost: "Lost",
    withdrawn: "Withdrawn",
};

const STAGE_ORDER = ["initial_review", "diagnosis_complete", "engaged", "structuring", "completed", "lost", "withdrawn"];

function StageBadge({ stage }) {
    const cls = STAGE_STYLES[stage] || STAGE_STYLES.identified;
    return (
        <Badge variant="outline" className={cls} data-testid={`mandate-stage-${stage}`}>
            {STAGE_LABELS[stage] || stage}
        </Badge>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB");
    } catch {
        return iso;
    }
}

// ─── Mandate card ─────────────────────────────────────────────────────────────

function MandateCard({ mandate }) {
    return (
        <Link
            to={`/distressed/mandates/${mandate.id}`}
            data-testid={`distressed-mandate-card-${mandate.id}`}
            className="border border-[#E2E8F0] rounded-[6px] bg-white px-5 py-4 flex items-center justify-between gap-4 hover:border-[#CBD5E1] transition-colors"
        >
            <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-[#0B1F3A] truncate">
                    {mandate.company_name}
                </p>
                <p className="text-[12px] text-[#64748B] mt-0.5">
                    {mandate.company_number}
                </p>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#94A3B8]">
                    <span>Created {fmtDate(mandate.created_at)}</span>
                    {mandate.watchlist_entry_id && (
                        <>
                            <span className="text-[#CBD5E1]">·</span>
                            <span>Promoted from watchlist</span>
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
                <StageBadge stage={mandate.stage} />
                <ArrowRight className="w-4 h-4 text-[#CBD5E1]" />
            </div>
        </Link>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DistressedMandates() {
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stageFilter, setStageFilter] = useState("_all");

    const load = async (stage) => {
        setLoading(true);
        try {
            const { data } = await api.get("/distressed/mandates", {
                params: stage && stage !== "_all" ? { stage } : {},
            });
            setMandates(data.mandates || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(stageFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageFilter]);

    return (
        <div className="p-6 space-y-6" data-testid="distressed-mandates-page">
            <PageHeader
                title="Distressed Mandates"
                subtitle="Live engagements — promoted from the watchlist or created directly."
            />

            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground max-w-2xl">
                    A mandate ties a distressed company to TUPE calculations, the rapid
                    diagnosis snapshot, a data room, and CDD status — everything a broker
                    needs to track the engagement in one place.
                </p>
                <Select value={stageFilter} onValueChange={setStageFilter}>
                    <SelectTrigger className="w-44" data-testid="mandate-stage-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All stages</SelectItem>
                        {STAGE_ORDER.map((s) => (
                            <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading mandates…
                </div>
            ) : mandates.length === 0 ? (
                <div className="border border-dashed border-[#E2E8F0] rounded-[6px] bg-white py-16 text-center">
                    <Briefcase className="w-6 h-6 mx-auto text-[#CBD5E1] mb-2" strokeWidth={1.5} />
                    <p className="text-sm text-muted-foreground">
                        No distressed mandates yet. Promote a company from the watchlist to start one.
                    </p>
                    <Button asChild variant="outline" size="sm" className="mt-3">
                        <Link to="/distressed/watchlist">Go to watchlist</Link>
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {mandates.map((m) => <MandateCard key={m.id} mandate={m} />)}
                </div>
            )}
        </div>
    );
}
